/**
 * Pair API client.
 *
 *   GET  /api/pairs                — enriched list (joins accounts, firm,
 *                                    last decision, DO live state)
 *   GET  /api/pairs/:id            — single pair DO state (raw DO body)
 *   POST /api/pairs/:id/mode       — change AI involvement level
 *   POST /api/pairs/:id/activate   — leave setup, pick mode
 */

import type { PairListItem, PairMode } from "@pfh/shared";
import { api } from "../api";

export type { PairListItem, PairMode } from "@pfh/shared";

export async function listPairs(): Promise<PairListItem[]> {
  const res = await api.get<{ pairs: PairListItem[] }>("/api/pairs");
  return res.pairs;
}

export async function getPair(idOrSlug: string): Promise<PairListItem | null> {
  // Existing /api/pairs/:id returns the DO `/state` JSON. We re-derive a
  // PairListItem from /api/pairs since it already enriches everything we
  // need; fetching the whole list is fine in v1 (operator has <20 pairs).
  //
  // Routes pass the opaque slug (preferred), but internal test pairs may
  // still be referenced by raw id — accept either.
  const all = await listPairs();
  return all.find((p) => p.slug === idOrSlug || p.id === idOrSlug) ?? null;
}

/**
 * Set or clear the per-symbol mode OVERRIDE.
 *
 * - `null`           clears the pin; the symbol falls back to inheriting
 *                    its parent account_pair's `ai_mode`.
 * - any `PairMode`   pins this symbol regardless of parent — the symbol
 *                    keeps the pinned mode even when the operator flips
 *                    the parent's ai_mode.
 *
 * Server resolves effective mode = override ?? parent.ai_mode and
 * persists both the override and the resolved value.
 */
export async function setPairModeOverride(
  idOrSlug: string,
  modeOverride: PairMode | null,
): Promise<void> {
  await api.post(`/api/pairs/${encodeURIComponent(idOrSlug)}/mode`, { modeOverride });
}

/**
 * Transition a freshly created pair from setup -> active with the
 * operator's chosen mode. The only way out of setup.
 */
export async function activatePair(
  idOrSlug: string,
  mode: PairMode = "manual",
): Promise<void> {
  await api.post(`/api/pairs/${encodeURIComponent(idOrSlug)}/activate`, {
    mode,
  });
}
